import React from 'react';
import PropTypes from 'prop-types';

// Shares styles with app level error boundary for same default error message
import styles from 'stylesheets/components/errors/error_boundary';

export class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // eslint-disable-next-line class-methods-use-this
  shouldReportError() {
    if (!window.Rollbar) {
      // Don't report error if Rollbar is not available/initialized
      return false;
    }

    if (window.BrowserSupported) {
      // If our BrowserSupported utility is available, check that this browser is supported.
      // Don't report errors for unsupported browsers if we know that they are unsupported.
      return window.BrowserSupported.supported();
    }

    return true;
  }

  componentDidCatch(error, errorInfo) {
    // Report to Rollbar if configured/loaded, otherwise log to console for development.
    if (this.shouldReportError()) {
      const appName = this.props.appName;
      Rollbar.error(`[${error.name}] ${error.message}`, { appName, error, errorInfo });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.hideOnError) {
      return null;
    }

    if (this.props.placeholderItem) {
      return this.props.placeholderItem;
    }

    return (
      <div className={styles.errorBoundary}>
        <h1 className={styles.errorBoundaryTitle}>Whoops! Something went wrong.</h1>
        <p className={styles.errorBoundaryText}>We&apos;ve logged the problem and are working on fixing it.</p>
        <p className={styles.errorBoundaryText}>
          Please
          <a className={styles.errorBoundaryLink} href='#' onClick={() => window.location.reload()}>
            refresh your browser
          </a>
          and try again.
        </p>
      </div>
    );
  }
}

ComponentErrorBoundary.propTypes = {
  appName: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  hideOnError: PropTypes.bool,
  placeholderItem: PropTypes.element,
};

ComponentErrorBoundary.defaultProps = {
  hideOnError: false,
  placeholderItem: null,
};
